import type { ComponentType, SVGProps } from "react";
import {
  IconActivity,
  IconBars,
  IconBell,
  IconCalendar,
  IconCheck,
  IconClipboard,
  IconCompass,
  IconDatabase,
  IconEdit,
  IconFlow,
  IconFrame,
  IconPie,
  IconPlug,
  IconReceipt,
  IconRss,
  IconSettings,
  IconTrending,
  IconWallet,
} from "@/platform/ui";

type IconType = ComponentType<SVGProps<SVGSVGElement> & { size?: number }>;

interface NavItem {
  to: string;
  label: string;
  icon: IconType;
}

interface NavGroup {
  heading: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    heading: "Investments",
    items: [
      { to: "/", label: "Market", icon: IconTrending },
      { to: "/portfolio", label: "Portfolio", icon: IconPie },
      { to: "/analytics", label: "Analysis", icon: IconBars },
      { to: "/strategy", label: "Strategy", icon: IconCompass },
    ],
  },
  {
    heading: "Finances",
    items: [
      { to: "/accounts", label: "Accounts", icon: IconWallet },
      { to: "/cash-flow", label: "Cash flow", icon: IconFlow },
      // Assigning categories to transactions — the frequent job. Managing the
      // taxonomy is rarer, and opens from this page's own Manage button.
      { to: "/categorize", label: "Categorize", icon: IconReceipt },
    ],
  },
  {
    heading: "Fitness",
    items: [
      { to: "/activity", label: "Activity", icon: IconActivity },
      // The nutrition library opens from a button on this page.
      { to: "/tracking", label: "Tracking", icon: IconClipboard },
    ],
  },
  {
    heading: "Planning",
    items: [
      { to: "/calendar", label: "Calendar", icon: IconCalendar },
      { to: "/tasks", label: "Tasks", icon: IconCheck },
      { to: "/notes", label: "Notes", icon: IconEdit },
      { to: "/feeds", label: "Feeds", icon: IconRss },
    ],
  },
  // Its own group despite holding one item, and deliberately so: everything
  // above tracks, measures or decides something. This doesn't. Filing it under
  // Planning would quietly make it another thing to get through.
  {
    heading: "Motivation",
    items: [{ to: "/art-gallery", label: "Art Gallery", icon: IconFrame }],
  },
  {
    heading: "Admin",
    items: [
      // One page, one nav entry: import and export answer one question —
      // what does this application know, and can I get it back out
      // (docs/imports.md, docs/exports.md).
      { to: "/data", label: "Import & Export", icon: IconDatabase },
      { to: "/notifications", label: "Notifications", icon: IconBell },
      // A marketplace-style roster of what's installed, not a settings
      // surface — above Settings for that reason, not filed under it.
      { to: "/extensions", label: "Extensions", icon: IconPlug },
      { to: "/settings", label: "Settings", icon: IconSettings },
    ],
  },
];

// Mobile bottom bar: a curated subset of NAV's own items — label/icon always
// come from NAV, so this only chooses which pages appear here and in what order.
const ALL_NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);
const MOBILE_PATHS = ["/", "/portfolio", "/tracking", "/feeds", "/art-gallery"];
export const MOBILE_TABS: NavItem[] = MOBILE_PATHS.map(
  (to) => ALL_NAV_ITEMS.find((item) => item.to === to)!,
);
